import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Tooltip,
  Button,
  useTheme,
  Stack,
  List,
  Typography,
  CircularProgress,
  TextField,
  Grid,
  Paper,
  Autocomplete,
  Box,
} from "@mui/material";
import { readAsyncStorageValues, readServiceTicketMessages, updateNotesForTicket } from "features/service/service.slice";
import moment from "moment";
import LanguageDropdown from "component/LanguageDropdown";
import { translateText } from "utils/utilFunctions";
import CardSkeleton from "component/Loaders/CardSkeleton";

const TicketNotes = ({ ticketId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [showDialog, setShowDialog] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const { serviceTicketById, serviceTicketMessages, appConstants, isLoading } = useSelector((state) => state.service);
  const [selectedLanguage, setSelectedLanguage] = useState({ value: "en", lang: "English" });
  const [serviceTicketMessageList, setServiceTicketMessageList] = useState([]);

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(readServiceTicketMessages(ticketId));
  }, [dispatch]);

  useEffect(() => {
    if (serviceTicketMessages) {
      setServiceTicketMessageList(serviceTicketMessages.filter((note) => note.type === appConstants.allTypes.TYPE_OF_CONTENT.TEXT));
    }
  }, [serviceTicketMessages]);

  const customeStyle = {
    p: 2,
    borderRadius: "10px",
    boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 4px 4px rgba(0,0,0,0.03)",
    mb: 1,
  };

  const handleNoteChange = (event) => {
    const inputValue = event.target.value;
    if (inputValue.length <= 50000) {
      setNewNote(inputValue);
      setErrorMsg("");
    } else {
      setErrorMsg(`Character limit reached (50000 characters max)`);
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    setNewNote("");
  };
  const checkAndTranslateText = async () => {
    const translatedDescription = await translateText(newNote || "");
    return translatedDescription[0]?.translatedText || "N.A";
  };
  const handleAddNote = async () => {
    if (newNote.length && newNote.trim()) {
      const message = await checkAndTranslateText();
      const postData = {
        serviceTicketId: ticketId,
        message: message,
      };
      dispatch(updateNotesForTicket(postData)).then((res) => dispatch(readServiceTicketMessages(ticketId)));
      setNewNote("");
      setShowDialog(false);
    } else setErrorMsg("*Please enter notes.");
  };
  // const onlangChange = async (newValue) => {
  //   if (newValue) {
  //     setSelectedLanguage(newValue);
  //     const list = await Promise.all(
  //       serviceTicketMessages.map(async (item) => {
  //         const translatedText = await translateText(item.message, newValue?.value || "en");
  //         return { ...item, message: translatedText[0].translatedText };
  //       })
  //     );
  //     setServiceTicketMessageList(list.filter((note) => note.type === appConstants.allTypes.TYPE_OF_CONTENT.TEXT));
  //   }
  // };

  if (isLoading)
    return (
      <div
        style={{
          textAlign: "center",
          paddingTop: 50,
          paddingBottom: 50,
        }}
      >
        <CardSkeleton count={3} />
      </div>
    );
  return (
    <div>
      <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" fontWeight={600} mb={1}>
          Notes
        </Typography>
        {/* <Box>
          <LanguageDropdown onlangChange={(newValue) => onlangChange(newValue)} selectedLanguage={selectedLanguage} />
        </Box> */}
        {serviceTicketById?.status != appConstants?.allTypes?.SERVICE_TICKET_STATUS.SUPERVISOR_APPROVED ? (
          <Tooltip title="Add note">
            <Button sx={{ height: "self" }} variant="contained" color="primary" onClick={() => setShowDialog(true)}>
              Add
            </Button>
          </Tooltip>
        ) : null}
      </Stack>
      <div>
        <Dialog open={showDialog} onClose={handleCancel}>
          <DialogTitle>Add note</DialogTitle>
          <DialogContent>
            <TextField
              value={newNote}
              label="Note"
              multiline
              onChange={handleNoteChange}
              error={errorMsg && !newNote.length}
              helperText={errorMsg || `${newNote.length}/50000`}
              sx={{ minWidth: 350, m: 2, maxWidth: 900, maxHeight: 700 }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button sx={{ height: "self" }} variant="contained" onClick={handleAddNote}>
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <Stack mb={1}>
        <List
          sx={{
            overflow: "auto",
            maxHeight: 500,
            p: 0,
          }}
        >
          {Object.keys(appConstants).length && serviceTicketMessageList.length ? (
            [...serviceTicketMessageList]
              .sort((a, b) => new Date(b.postedDate || b.dateCreated).getTime() - new Date(a.postedDate || a.dateCreated).getTime())
              .map((note, idx) =>
                note.type === appConstants.allTypes.TYPE_OF_CONTENT.TEXT ? (
                  <Paper
                    elevation={3}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      background: theme.palette.mode === "dark" ? "#23272f" : "#f9f9fb",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                      p: 2,
                      minWidth: 220,
                      borderLeft: `6px solid ${theme.palette.primary.main}`,
                      transition: "box-shadow 0.2s",
                      "&:hover": {
                        boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
                      },
                    }}
                    key={note.id || idx}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography color="primary" fontWeight={700} sx={{ textTransform: "none" }}>
                        <span style={{ textTransform: "capitalize" }}>{(note.postedUser?.name || "-").slice(0, 12)}</span>
                        <span>{` (${(note.postedUser?.username || "").toLowerCase()})`}</span>
                      </Typography>
                      <Typography color="text.secondary" sx={{ ml: 1 }}>
                        {note?.postedDate || note?.dateCreated
                          ? moment(note?.postedDate ? note.postedDate : note.dateCreated).format("DD MMM YY, HH:mm")
                          : "N.A"}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: 15,
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                        mb: 0.5,
                      }}
                    >
                      {note.message}
                    </Typography>
                  </Paper>
                ) : null
              )
          ) : (
            <Paper elevation={0} sx={{ p: 3, textAlign: "center", color: theme.palette.text.disabled, background: "#f5f5f7" }}>
              No notes.
            </Paper>
          )}
        </List>
      </Stack>
    </div>
  );
};

export default TicketNotes;
